<template>
  <div class="profile-container">
    <h1 class="header-1">{{ $t('profile.title') }}</h1>
    <p class="description">{{ $t('profile.description') }}</p>

    <div v-if="isLoading" class="loading-state">
      <div class="loading-spinner"></div>
      <p>{{ $t('profile.loading') }}</p>
    </div>

    <div v-else-if="error" class="error-state">
      <BaseIcon iconName="alert" class="error-icon" />
      <h3>{{ $t('profile.loadFailed') }}</h3>
      <p>{{ error }}</p>
      <button class="retry-btn" @click="retryLoading">{{ $t('common.retry') }}</button>
    </div>

    <div v-else-if="!userDetails" class="error-state">
      <BaseIcon iconName="alert" class="error-icon" />
      <h3>{{ $t('profile.noDetails') }}</h3>
      <p>{{ $t('profile.noDetailsDescription') }}</p>
      <button class="retry-btn" @click="retryLoading">{{ $t('common.retry') }}</button>
    </div>

    <div v-else class="content">
      <div class="account-details">
        <div class="detail">
          <BaseIcon iconName="person" class="icon" color="rgba(255, 255, 255, 0.4)" />
          <div class="detail-info">
            <div class="label">{{ $t('profile.name') }}</div>
            <div class="value">
              <BaseOutput
                :model-value="userDetails.user_name"
                :show-actions="true"
                :placeholder="$t('profile.namePlaceholder')"
                :empty-text="$t('profile.notSet')"
                @save="handleNameChange"
              />
            </div>
          </div>
        </div>

        <div class="detail">
          <BaseIcon iconName="envelop" class="icon" color="rgba(255, 255, 255, 0.4)" />
          <div class="detail-info">
            <div class="label">{{ $t('auth.email') }}</div>
            <div class="value">{{ email }}</div>
          </div>
        </div>

        <div class="detail">
          <BaseIcon iconName="credit" class="icon" color="rgba(255, 255, 255, 0.4)" />
          <div class="detail-info">
            <div class="label">{{ $t('profile.credits') }}</div>
            <div class="value">{{ $t('profile.creditsOf', { current: remainingCredits, max: maxCredits }) }}</div>
          </div>
        </div>

        <div
          class="detail"
          v-if="userDetails.expiry_date && userDetails.expiry_date !== 'None'"
        >
          <BaseIcon iconName="calendar" class="icon" color="rgba(255, 255, 255, 0.4)" />
          <div class="detail-info">
            <div class="label">{{ $t('profile.nextBilling') }}</div>
            <div class="value">{{ formatExpiryDate(userDetails.expiry_date) }}</div>
          </div>
        </div>

        <div class="detail">
          <BaseIcon iconName="lock" class="icon" color="rgba(255, 255, 255, 0.4)" />
          <div class="detail-info">
            <div class="label">{{ $t('profile.password') }}</div>
            <div class="value">
              <SecondBtn @click="handleResetPassword" :disabled="isResettingPassword">
                {{ isResettingPassword ? $t('profile.sending') : $t('profile.resetPassword') }}
              </SecondBtn>
            </div>
          </div>
        </div>
      </div>

      <!-- Right: Plan Summary -->
      <div class="plan-details">
        <h2 class="header-2">{{ $t('profile.planSummary') }}</h2>
        <PlanSummary
          :planName="currentPlan.displayName"
          :status="getSubscriptionStatus"
          :subscriptionId="userDetails.subscription_id"
          :expiryDate="userDetails.expiry_date"
        />
      </div>
    </div>

    <!-- Actions: Upgrade, Logout, Cancel -->
    <div class="actions" v-if="userDetails">
      <router-link to="/plans">
        <PrimaryBtn class="upgrade-btn">{{ $t('profile.upgrade') }}</PrimaryBtn>
      </router-link>
      <div class="bottom-actions">
        <button class="logout-btn" @click="handleLogout">{{ $t('profile.logout') }}</button>
        <button
          class="cancel-btn"
          @click="handleCancelSubscription"
          v-if="showCancelSubscription"
        >
          {{ $t('profile.cancelSubscription') }}
        </button>
      </div>
    </div>

    <!-- Notification Toast -->
    <NotificationToast
      :show="showNotification"
      :message="notificationMessage"
      :is-error="isErrorNotification"
      @close="closeNotification"
    />
  </div>
</template>

<script setup>
import { onMounted, computed, ref } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import PrimaryBtn from "@/components/BaseComponents/PrimaryBtn.vue";
import BaseIcon from "@/components/BaseComponents/BaseIcon.vue";
import PlanSummary from "@/components/PlanSummary.vue";
import BaseOutput from "@/components/BaseComponents/BaseOutput.vue";
import SecondBtn from "@/components/BaseComponents/SecondBtn.vue";
import NotificationToast from "@/components/BaseComponents/NotificationToast.vue";
import { plans, subscriptionPlanMap } from "@/constants/plans";
import { userAPI } from "@/services/api";
import { authAPI } from "@/services/api";
import { useI18n } from 'vue-i18n';

const store = useStore();
const router = useRouter();
const { locale, t } = useI18n();
const isLoading = ref(true);
const isResettingPassword = ref(false);
const error = ref(null);

// Notification state
const showNotification = ref(false);
const notificationMessage = ref("");
const isErrorNotification = ref(false);

async function loadUserData() {
  isLoading.value = true;
  error.value = null;
  try {
    await Promise.all([
      store.dispatch("fetchUserDetails"),
      store.dispatch("fetchRemainingCredits"),
    ]);
  } catch (err) {
    error.value = err.message || t('profile.loadFailed');
    console.error("Error loading user data:", err);
  } finally {
    isLoading.value = false;
  }
}

async function retryLoading() {
  await loadUserData();
}

onMounted(() => {
  loadUserData();
});


const userDetails = computed(() => store.getters.getUserDetails);
const email = computed(() => store.getters.getEmail);

const currentPlan = computed(() => {
  const currentPlanName = userDetails.value?.current_subscription_plan || "Freemium";
  const mappedPlanKey = subscriptionPlanMap[currentPlanName];
  const planDetails = plans[mappedPlanKey || "Freemium"];

  return {
    ...planDetails,
    displayName: mappedPlanKey,
  };
});

const maxCredits = computed(() => {
  const planKey =
    subscriptionPlanMap[userDetails.value?.current_subscription_plan] || "Freemium";
  return plans[planKey]?.creditsByDefault?.toLocaleString() || "0";
});

const remainingCredits = computed(() => {
  const credits = store.getters.getRemainingCredits;
  return Number(credits).toLocaleString();
});

const getSubscriptionStatus = computed(() => {
  if (!userDetails.value) return t('profile.unknown');
  if (!userDetails.value.current_subscription_plan) return t('profile.freePlan');
  if (
    !userDetails.value.expiry_date ||
    userDetails.value.expiry_date === "None" ||
    userDetails.value.expiry_date === null
  )
    return t('profile.inactive');
  const expiryDate = new Date(userDetails.value.expiry_date);
  return expiryDate > new Date() ? t('profile.active') : t('profile.expired');
});

const showCancelSubscription = computed(() => {
  if (!userDetails.value) return false;

  // Don't show cancel for freemium users
  const currentPlanName = userDetails.value.current_subscription_plan;
  if (!currentPlanName || currentPlanName === 'Freemium') return false;

  // Don't show cancel if no subscription ID
  if (!userDetails.value.subscription_id || userDetails.value.subscription_id === 'None') {
    return false;
  }

  // Show cancel button for paid subscriptions
  return true;
});

const formatExpiryDate = (dateString) => {
  if (!dateString || dateString === "None") return t('profile.notAvailable');
  const date = new Date(dateString);
  return date.toLocaleDateString(locale.value, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const handleLogout = () => {
  store.dispatch("logout");
};

const handleCancelSubscription = () => {
  router.push("/subcancel");
};

async function handleNameChange(newName) {
  try {
    await userAPI.changeUserName(store.getters.getSessionId, newName);
    await loadUserData();
    showNotification.value = true;
    notificationMessage.value = t('profile.nameUpdated');
    isErrorNotification.value = false;
  } catch (error) {
    console.error("Error updating name:", error);
    showNotification.value = true;
    notificationMessage.value = t('profile.nameUpdateFailed', { error: error.message || t('profile.unknown') });
    isErrorNotification.value = true;
  }
}

function closeNotification() {
  showNotification.value = false;
}

const handleResetPassword = async () => {
  try {
    isResettingPassword.value = true;
    await authAPI.resetPasswordEmail(email.value);
    router.push({
      path: "/resetpass/confirm",
      query: { fromProfile: "true" },
    });
  } catch (error) {
    console.error("Error requesting password reset:", error);
  } finally {
    isResettingPassword.value = false;
  }
};
</script>

<style scoped>
.profile-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: var(--bg-card);
  width: 80%;
  max-width: 900px;
  padding: var(--spacing-xl);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-card);
  text-align: center;
}

.description {
  color: var(--color-white-60);
  margin: 0;
  padding: 0 35%;
}

.content {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: var(--spacing-4xl);
  text-align: left;
}

.account-details {
  flex: 1;
  padding: var(--spacing-notification);
  position: relative;
}

.account-details::after {
  content: "";
  position: absolute;
  top: var(--spacing-lg);
  right: 0;
  width: 1px;
  height: calc(100% - (var(--spacing-lg) * 2));
  background: var(--color-white-10);
}

.plan-details {
  flex: 1;
  padding: var(--spacing-notification);
}

.detail {
  display: flex;
  align-items: center;
  gap: var(--spacing-xl);
  padding: 0;
  min-height: 56px;
}

.detail-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.label {
  color: var(--color-white-40);
  font-size: var(--font-size-body-sm);
  line-height: var(--line-height-tight);
}

.value {
  color: var(--color-white-90);
  font-weight: 500;
  line-height: var(--line-height-tight);
}

.value :deep(.base-output) {
  min-height: auto;
}

.value :deep(.display-mode),
.value :deep(.edit-mode) {
  min-height: auto;
  padding: 0;
}

.actions {
  width: 100%;
  margin-top: var(--spacing-3xl);
  text-align: center;
}

.upgrade-btn {
  width: 200px;
}

.bottom-actions {
  display: flex;
  justify-content: center;
  position: relative;
  width: 100%;
  margin-top: var(--spacing-notification);
}

.logout-btn,
.cancel-btn {
  background: none;
  border: none;
  color: var(--color-white-40);
  cursor: pointer;
  font-size: var(--font-size-caption);
  transition: var(--transition-normal);
  padding: var(--padding-btn-md);
  border-radius: var(--border-radius-xs);
}

.cancel-btn {
  position: absolute;
  right: 0;
}

.logout-btn:hover {
  color: var(--color-cancel);
  background: var(--color-cancel-10);
}

.cancel-btn:hover {
  color: var(--color-cancel);
  background: var(--color-cancel-10);
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-4xl);
  text-align: center;
  border-radius: var(--border-radius-lg);
  margin: var(--spacing-notification) 0;
  width: 100%;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--bg-card);
  border-radius: var(--border-radius-circle);
  border-top-color: var(--color-success);
  border-right-color: var(--color-lime);
  animation: spin 1s linear infinite;
  margin-bottom: var(--spacing-notification);
}

.error-icon {
  font-size: 32px;
  color: var(--color-cancel);
  margin-bottom: var(--spacing-xl);
}

.retry-btn {
  margin-top: var(--spacing-xl);
  padding: var(--padding-btn-lg);
  border-radius: var(--border-radius-md);
  border: none;
  background: var(--gradient-success);
  color: var(--color-white);
  cursor: pointer;
  font-weight: 500;
  transition: var(--transition-normal);
}

.retry-btn:hover {
  opacity: var(--opacity-hover);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.reset-password-btn {
  background: none;
  border: none;
  color: var(--color-success);
  cursor: pointer;
  padding: 0;
  font-size: inherit;
  font-weight: 500;
  transition: var(--transition-normal);
}

.reset-password-btn:hover {
  opacity: var(--opacity-hover-light);
}

@media (max-width: 850px) {
  .content {
    flex-direction: column;
  }

  .account-details {
    border-bottom: 1px solid var(--color-white-10);
  }

  .account-details::after {
    display: none;
  }

  .description {
    padding: 0 10%;
  }

  .profile-container {
    margin: var(--spacing-3xl) 0;
  }
}
</style>
